import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import Swal from "sweetalert2";
import { amenityService } from "../../../service/amenity.service";
import { Amenity } from "../../../models/amenity.model";
import { useLocation } from "react-router-dom";
import { set } from "react-hook-form";

// Initial state for a child amenity
const initialChildAmenity: Amenity = {
  id: 0,
  name: "",
  description: "",
  iconName: "",
  parentAmenityId: 0,
};

const ChildAmenitiesComponent: React.FC = () => {
  const [childAmenities, setChildAmenities] = useState<Amenity[]>([]);
  const [childAmenity, setChildAmenity] = useState<Amenity>(initialChildAmenity);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { amenityName, amenityIdFromState } = location.state || {};
  const [selectedAmenity, setSelectedAmenity] = useState<number | null>(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    if (amenityIdFromState) {
      fetchChildAmenities(amenityIdFromState);
    }
  }, [amenityIdFromState]);

  // Fetch child amenities
  const fetchChildAmenities = async (parentId: number) => {
    setListLoading(true);
    try {
      const result = await amenityService.getChildAmenitiesByParentId(parentId);
      setChildAmenities(result ?? []);
    } catch (error) {
      console.error("Failed to fetch child amenities:", error);
      setChildAmenities([]);
    } finally {
      setListLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setChildAmenity(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Link child amenity to parent
      const payload = { ...childAmenity, parentAmenityId: amenityIdFromState ?? childAmenity.parentAmenityId };
      if (childAmenity.id) {
        const updated = await amenityService.update(childAmenity.id, payload);
        setChildAmenities(prev => prev.map(c => (c.id === childAmenity.id ? updated : c)));
        Swal.fire({ toast: true, position: 'bottom', icon: 'success', title: 'Child Amenity updated successfully.', showConfirmButton: false, timer: 3000, timerProgressBar: true });
      } else {
        const created = await amenityService.create(payload);
        setChildAmenities(prev => [...prev, created]);
        Swal.fire({ toast: true, position: 'bottom', icon: 'success', title: 'Child Amenity created successfully.', showConfirmButton: false, timer: 3000, timerProgressBar: true });
      }
      setChildAmenity(initialChildAmenity);
      setShowCreateForm(false);
    } catch (err: any) {
      Swal.fire({ toast: true, position: 'bottom', icon: 'error', title: 'Failed to save child amenity.', showConfirmButton: false, timer: 3000, timerProgressBar: true });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rowData: Amenity) => {
    setChildAmenity(rowData);
    setSelectedAmenity(rowData.parentAmenityId);
    setShowCreateForm(true);
  };

// Add delete handler
const handleDelete = async (rowData: Amenity) => {
  if (!rowData.id) return;
  const result = await Swal.fire({
    title: "Are you sure?",
    text: "Do you want to delete this child amenity?",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Yes, delete it!",
    cancelButtonText: "Cancel",
  });

  if (result.isConfirmed) {
    setLoading(true);
    try {
      await amenityService.delete(rowData.id);
      setChildAmenities(prev => prev.filter(c => c.id !== rowData.id));
      Swal.fire({ toast: true, position: 'bottom', icon: 'success', title: 'Child Amenity deleted successfully.', showConfirmButton: false, timer: 3000, timerProgressBar: true });
    } catch {
      Swal.fire({ toast: true, position: 'bottom', icon: 'error', title: 'Failed to delete child amenity.', showConfirmButton: false, timer: 3000, timerProgressBar: true });
    } finally {
      setLoading(false);
    }
  }
};

  const actionBodyTemplate = (rowData: Amenity) => (
    <div className="d-flex align-items-center gap-2">
      <Button variant="link" className="p-0" onClick={() => handleEdit(rowData)} title="Edit" style={{ color: '#198754' }}>
        <Icon.PencilSquare size={20} />
      </Button>
      <Button variant="link" className="p-0" onClick={() => handleDelete(rowData)} title="Delete" style={{ color: '#dc3545' }}>
        <Icon.Trash size={20} />
      </Button>
    </div>
  );

  return (
    <div className="content has-page-header">
      <div className="content-header mb-4">
        <h2>{`Child Amenities under - ${amenityName}`}</h2>
      </div>
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center gap-2 my-3 flex-wrap">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon><Icon.Search className="tx-12" /></InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={e => setGlobalFilter(e.target.value)}
                    placeholder="Search"
                    style={{ minWidth: 250 }}
                  />
                </IconField>
                <Button className="btn btn-primary ms-2" onClick={() => {
                  setChildAmenity({
                    ...initialChildAmenity,
                    parentAmenityId: amenityIdFromState ?? 0, // Set parent id from navigation state
                  });
                  setShowCreateForm(true);
                }}>
                  <Icon.PlusCircle className="me-1" /> Create Child Amenity
                </Button>
              </div>

              <DataTable
                value={childAmenities.filter(c => c.name.toLowerCase().includes(globalFilter.toLowerCase()))}
                dataKey="id"
                paginator
                rows={10}
                loading={listLoading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage={selectedAmenity ? "No child amenities found for the selected amenity." : "Please select an amenity to load child amenities."}
                className="grid-table"
              >
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                {/* <Column field="amenityId" header="Amenity ID" sortable /> */}
                <Column header="Actions" body={actionBodyTemplate} style={{ minWidth: '120px' }} />
              </DataTable>

              {showCreateForm && (
                <div className="modal show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.3)' }}>
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">{childAmenity.id ? "Update Child Amenity" : "Create Child Amenity"}</h5>
                        <button type="button" className="btn-close" onClick={() => setShowCreateForm(false)}></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Name</label>
                            <input
                              className="form-control"
                              name="name"
                              value={childAmenity.name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Description</label>
                            <input
                              className="form-control"
                              name="description"
                              value={childAmenity.description}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? (childAmenity.id ? "Updating..." : "Creating...") : (childAmenity.id ? "Update" : "Create")}
                          </button>
                          <button type="button" className="btn btn-secondary" onClick={() => setShowCreateForm(false)}>Cancel</button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChildAmenitiesComponent;